/**
 * Volier hero scent wheel.
 * Turns the arch cards like clock hands every `data-interval` seconds: all cards hang from one
 * pivot below the wheel and travel clockwise. The active card stands at 12 o'clock, the next one
 * waits on the left, the previous one leaves on the right and the rest hide under the pivot.
 * Autoplay pauses while the user hovers/focuses the wheel, when it is off-screen or the
 * tab is hidden, and never starts under prefers-reduced-motion.
 */
/**
 * Soft golden perfume mist drawn on a canvas. Particles are tinted sprites that drift, grow and
 * fade; the animation loop only runs while particles are alive.
 */
class VolierMist {
  #particles = [];
  #sprites = new Map();
  #frame = 0;
  #dpr = 1;

  constructor(canvas, light = false) {
    this.light = light;
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.resize = this.resize.bind(this);
    this.tick = this.tick.bind(this);
    this.observer = new ResizeObserver(this.resize);
    this.observer.observe(canvas);
    this.resize();
  }

  destroy() {
    this.observer.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  resize() {
    this.#dpr = Math.min(window.devicePixelRatio || 1, 2);
    const { width, height } = this.canvas.getBoundingClientRect();
    this.canvas.width = Math.round(width * this.#dpr);
    this.canvas.height = Math.round(height * this.#dpr);
  }

  /** Cached radial sprite per color ("rgba(r, g, b, 1)"); `hard` gives a crisp droplet */
  #sprite(color, hard = false) {
    const key = `${color}${hard ? ':hard' : ''}`;
    if (this.#sprites.has(key)) return this.#sprites.get(key);
    const size = 64;
    const sprite = document.createElement('canvas');
    sprite.width = sprite.height = size;
    const g = sprite.getContext('2d');
    const gradient = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    const rgb = color.slice(0, color.lastIndexOf(','));
    gradient.addColorStop(0, `${rgb}, 1)`);
    gradient.addColorStop(hard ? 0.6 : 0.35, `${rgb}, ${hard ? 0.9 : 0.35})`);
    gradient.addColorStop(1, `${rgb}, 0)`);
    g.fillStyle = gradient;
    g.fillRect(0, 0, size, size);
    this.#sprites.set(key, sprite);
    return sprite;
  }

  /** Burst of mist from (x, y) in CSS pixels, heading towards `direction` (1 = right, -1 = left). */
  spray(x, y, direction = 1, tint = 'rgba(234, 219, 198, 1)', amount = 70) {
    // Light hero: the perfume's own color, a paler version of it and warm sand; never dark
    const palette = this.light
      ? [tint, VolierMist.mix(tint, 0.45), 'rgba(212, 202, 188, 1)']
      : [tint, 'rgba(240, 228, 210, 1)', 'rgba(201, 169, 135, 1)'];
    if (this.light) this.#droplets(x, y, direction, palette, Math.round(amount * 0.4));
    for (let i = 0; i < amount; i++) {
      const angle = (-8 + (Math.random() - 0.5) * 50) * (Math.PI / 180);
      const speed = this.light ? 1.2 + Math.random() * 3.4 : 1.4 + Math.random() * 4.6;
      this.#particles.push({
        x: x + direction * 4,
        y,
        vx: Math.cos(angle) * speed * direction,
        vy: Math.sin(angle) * speed,
        size: this.light ? 3 + Math.random() * 6 : 2.5 + Math.random() * 6,
        grow: this.light ? 1.014 + Math.random() * 0.014 : 1.018 + Math.random() * 0.02,
        life: 0,
        maxLife: this.light ? 55 + Math.random() * 60 : 60 + Math.random() * 80,
        alpha: this.light ? 0.05 + Math.random() * 0.09 : 0.06 + Math.random() * 0.16,
        color: palette[i % 3],
        delay: Math.random() * 10,
      });
    }
    if (!this.#frame) this.#frame = requestAnimationFrame(this.tick);
  }

  /** Fine, crisp droplets that shoot out faster than the mist and arc down */
  #droplets(x, y, direction, palette, amount) {
    for (let i = 0; i < amount; i++) {
      const angle = (-14 + (Math.random() - 0.5) * 40) * (Math.PI / 180);
      const speed = 2 + Math.random() * 4.5;
      this.#particles.push({
        x: x + direction * 4,
        y,
        vx: Math.cos(angle) * speed * direction,
        vy: Math.sin(angle) * speed,
        size: 0.8 + Math.random() * 1.6,
        grow: 1,
        gravity: 0.05 + Math.random() * 0.05,
        life: 0,
        maxLife: 30 + Math.random() * 35,
        alpha: 0.25 + Math.random() * 0.3,
        color: palette[i % 2],
        hard: true,
        delay: Math.random() * 6,
      });
    }
  }

  /** Blend an "rgba(r, g, b, 1)" color towards white by `amount` (0..1) */
  static mix(color, amount) {
    const [r, g, b] = color.match(/\d+/g).map(Number);
    const m = (v) => Math.round(v + (255 - v) * amount);
    return `rgba(${m(r)}, ${m(g)}, ${m(b)}, 1)`;
  }

  tick() {
    const { ctx } = this;
    ctx.setTransform(this.#dpr, 0, 0, this.#dpr, 0, 0);
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.globalCompositeOperation = this.light ? 'source-over' : 'lighter';

    this.#particles = this.#particles.filter((p) => {
      if (p.delay > 0) {
        p.delay -= 1;
        return true;
      }
      p.life += 1;
      p.x += p.vx;
      p.y += p.vy;
      p.vx *= 0.955;
      p.vy = p.hard ? p.vy * 0.97 + p.gravity : p.vy * 0.955 - 0.012;
      p.size *= p.grow;
      const t = p.life / p.maxLife;
      const fade = t < 0.15 ? t / 0.15 : 1 - (t - 0.15) / 0.85;
      ctx.globalAlpha = Math.max(0, p.alpha * fade);
      ctx.drawImage(this.#sprite(p.color, p.hard), p.x - p.size, p.y - p.size, p.size * 2, p.size * 2);
      return p.life < p.maxLife;
    });

    ctx.globalAlpha = 1;
    this.#frame = this.#particles.length ? requestAnimationFrame(this.tick) : 0;
  }
}

class VolierHero extends HTMLElement {
  #index = 0;
  #timer = 0;
  #visible = true;
  #hovering = false;
  #userPaused = false;
  #pointerStartX = null;
  #swiped = false;
  #observer = null;
  #ready = false;
  #reducedMotion = false;
  #sprayDirection = 1;
  /** Current angle of each card in degrees; never wrapped, so turns keep their direction */
  #angles = [];
  #clockwise = true;

  connectedCallback() {
    this.cards = [...this.querySelectorAll('[data-scent-card]')];
    this.details = [...this.querySelectorAll('[data-scent-detail]')];
    this.dots = [...this.querySelectorAll('[data-scent-dot]')];
    this.wheel = this.querySelector('[data-wheel]');
    this.pauseButton = this.querySelector('[data-pause]');
    this.interval = (Number(this.dataset.interval) || 3) * 1000;

    if (this.cards.length === 0) return;

    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.#reducedMotion) this.#setUserPaused(true);

    const canvas = this.querySelector('[data-mist]');
    if (canvas && !this.#reducedMotion) this.mist = new VolierMist(canvas, this.classList.contains('vh--light'));

    this.cards.forEach((card, i) =>
      card.addEventListener('click', () => {
        if (this.#swiped) return (this.#swiped = false);
        // One bottle at a time (solo): a tap on it turns the wheel to the next perfume
        if (i === this.#index && !this.classList.contains('vh--solo')) this.#spray();
        else this.go(i === this.#index ? i + 1 : i);
      })
    );
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));
    this.pauseButton?.addEventListener('click', () => this.#setUserPaused(!this.#userPaused));

    this.wheel.addEventListener('keydown', this.#onKeydown);
    this.wheel.addEventListener('dragstart', (e) => e.preventDefault());
    this.wheel.addEventListener('pointerdown', this.#onPointerDown);
    this.wheel.addEventListener('pointerup', this.#onPointerUp);
    this.wheel.addEventListener('pointercancel', () => (this.#pointerStartX = null));
    // Mouse hover and keyboard focus pause autoplay; touch taps must not (no matching leave event).
    this.wheel.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && this.#setHovering(true));
    this.wheel.addEventListener('pointerleave', (e) => e.pointerType === 'mouse' && this.#setHovering(false));
    this.addEventListener('focusin', (e) => e.target.matches(':focus-visible') && this.#setHovering(true));
    this.addEventListener('focusout', () => this.#setHovering(false));
    document.addEventListener('visibilitychange', this.#schedule);
    document.addEventListener('shopify:block:select', this.#onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.#onBlockDeselect);

    this.#observer = new IntersectionObserver(([entry]) => {
      this.#visible = entry.isIntersecting;
      this.#schedule();
    });
    this.#observer.observe(this);

    this.#render();

    // Wait for the page intro (if it is playing) before the first spray and autoplay
    if (document.documentElement.classList.contains('vi-active')) {
      document.addEventListener('volier:intro-done', () => this.#start(), { once: true });
    } else {
      this.#start();
    }
  }

  #start() {
    if (this.#ready) return;
    this.#ready = true;
    this.classList.add('is-ready');
    setTimeout(() => this.#spray(), 450);
    this.#schedule();
  }

  /** Mist from the atomizer of the active bottle, alternating sides */
  #spray(amount = this.classList.contains('vh--light') ? 55 : 100) {
    if (!this.mist) return;
    const card = this.cards[this.#index];
    // Use layout geometry (ignores the running transform) so the mist leaves the atomizer
    // where the bottle will rest, even while the wheel is still turning.
    const hero = this.getBoundingClientRect();
    const wheel = this.wheel.getBoundingClientRect();
    const x = wheel.left - hero.left + card.offsetLeft;
    const y = wheel.top - hero.top + card.offsetTop + card.offsetHeight * 0.14;
    const light = this.classList.contains('vh--light');
    const tint = this.#tint(light ? card.dataset.liquid : card.style.getPropertyValue('--c2').trim());
    this.#sprayDirection *= -1;
    this.mist.spray(x + this.#sprayDirection * card.offsetWidth * 0.07, y, this.#sprayDirection, tint, amount);
  }

  #tint(hex) {
    const match = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex);
    if (!match) return 'rgba(234, 219, 198, 1)';
    // Dark hero: lighten the card color towards cream. Light hero: the perfume color as it is.
    const light = this.classList.contains('vh--light');
    const [r, g, b] = match
      .slice(1)
      .map((v) => Math.round(light ? parseInt(v, 16) : parseInt(v, 16) * 0.35 + 234 * 0.65));
    return `rgba(${r}, ${g}, ${b}, 1)`;
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.mist?.destroy();
    this.#observer?.disconnect();
    document.removeEventListener('visibilitychange', this.#schedule);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.#onBlockDeselect);
  }

  go(index) {
    const count = this.cards.length;
    const next = ((index % count) + count) % count;
    // Shortest way round; ties and autoplay go clockwise
    const forward = (next - this.#index + count) % count;
    this.#clockwise = forward <= count - forward;
    this.#index = next;
    this.#render();
    if (this.#ready) setTimeout(() => this.#spray(), 520);
  }

  #render() {
    const count = this.cards.length;

    this.cards.forEach((card, i) => {
      const offset = (i - this.#index + count) % count;
      // The next card waits on the left so that advancing carries it clockwise to the top
      let position = 'back';
      if (offset === 0) position = 'center';
      else if (offset === 1) position = 'left';
      else if (offset === count - 1) position = 'right';

      // Reach the slot's angle (0, 28, 180 or -28) without ever turning back
      const slot = { center: 0, right: 28, back: 180, left: 332 }[position];
      const current = this.#angles[i];
      let angle = slot;
      if (current !== undefined) {
        const turn = Math.floor(current / 360) * 360 + slot;
        if (this.#clockwise) angle = turn < current ? turn + 360 : turn;
        else angle = turn > current ? turn - 360 : turn;
      } else if (position === 'left') {
        angle = -28;
      }
      this.#angles[i] = angle;
      card.style.setProperty('--a', `${angle}deg`);

      card.dataset.pos = position;
      card.tabIndex = position === 'center' ? 0 : -1;
      card.setAttribute('aria-current', String(position === 'center'));
    });

    this.details.forEach((detail, i) => {
      const active = i === this.#index;
      detail.classList.toggle('is-active', active);
      detail.toggleAttribute('inert', !active);
    });

    this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.#index)));

    const glow = this.cards[this.#index].dataset.glow;
    if (glow) this.style.setProperty('--vh-glow', glow);

    this.#schedule();
  }

  #schedule = () => {
    clearTimeout(this.#timer);
    const canPlay =
      this.#ready && this.cards.length > 1 && !this.#userPaused && !this.#hovering && this.#visible && !document.hidden;
    if (canPlay) this.#timer = setTimeout(() => this.go(this.#index + 1), this.interval);
  };

  #setHovering(value) {
    this.#hovering = value;
    this.#schedule();
  }

  #setUserPaused(value) {
    this.#userPaused = value;
    if (this.pauseButton) {
      this.pauseButton.setAttribute('aria-pressed', String(value));
      this.pauseButton.setAttribute('aria-label', value ? 'Reproducir' : 'Pausar');
    }
    this.#schedule();
  }

  #onKeydown = (event) => {
    if (event.key === 'ArrowRight') this.go(this.#index + 1);
    else if (event.key === 'ArrowLeft') this.go(this.#index - 1);
    else return;
    event.preventDefault();
  };

  #onPointerDown = (event) => {
    this.#pointerStartX = event.clientX;
    this.#swiped = false;
  };

  #onPointerUp = (event) => {
    if (this.#pointerStartX === null) return;
    const delta = event.clientX - this.#pointerStartX;
    this.#pointerStartX = null;
    if (Math.abs(delta) < 40) return;
    this.#swiped = true;
    // Dragging to the right pushes the wheel clockwise
    this.go(this.#index + (delta > 0 ? 1 : -1));
  };

  #onBlockSelect = (event) => {
    const index = this.cards.indexOf(event.target);
    if (index === -1) return;
    this.#hovering = true;
    this.go(index);
  };

  #onBlockDeselect = (event) => {
    if (this.cards.includes(event.target)) this.#setHovering(false);
  };
}

if (!customElements.get('volier-hero')) customElements.define('volier-hero', VolierHero);
